/**
 * Append-only activity log for a contact.
 *
 * Model distinction: `contact.notes` (edited in ContactEditCard) is standing
 * context that gets overwritten in place — the current state of the
 * relationship. This timeline, backed by `crm_activities`, is an
 * append-only history of what happened over time — a different model.
 * Before this component, `useActivities` had zero importers and
 * `crm_activities` had 0 rows in production, because nothing read it.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useActivities, useLogActivity } from '@/hooks/crm/useContacts';
import type { Activity } from '@/lib/crm/types';
import { formatUiRelativeTime } from '@/lib/ui-format';

function formatActivityType(activityType: string): string {
  return activityType
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** The stored message text, if this activity carries one. `metadata` is jsonb. */
function activityBody(activity: Activity): string | null {
  const body = (activity.metadata as { body?: unknown } | undefined)?.body;
  return typeof body === 'string' && body.trim() !== '' ? body : null;
}

export interface ActivityTimelineProps {
  contactId: string;
}

export function ActivityTimeline({ contactId }: ActivityTimelineProps): JSX.Element {
  const [noteText, setNoteText] = useState('');
  const activitiesQuery = useActivities(contactId);
  const logActivity = useLogActivity();

  // `crmApi.getActivities` is typed `Promise<any[]>` at the API boundary;
  // narrow it once here rather than letting `any` leak into this component.
  const activities = (activitiesQuery.data ?? []) as Activity[];

  const handleAddNote = (): void => {
    const description = noteText.trim();
    if (!description) return;

    logActivity.mutate(
      { contactId, activityType: 'note_added', description },
      { onSuccess: () => setNoteText('') },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Activity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="activity-timeline-note">Add a note</Label>
          <Textarea
            id="activity-timeline-note"
            rows={2}
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            data-content-origin="user"
          />
          <Button
            type="button"
            onClick={handleAddNote}
            disabled={!noteText.trim() || logActivity.isPending}
            className="min-h-11 md:min-h-11"
          >
            Add note
          </Button>
        </div>

        {activitiesQuery.isLoading && (
          <div className="space-y-3" role="status" aria-label="Loading activity">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {activitiesQuery.isError && (
          <p className="text-sm text-destructive" role="alert">
            Failed to load activity. Please try again.
          </p>
        )}

        {!activitiesQuery.isLoading && !activitiesQuery.isError && activities.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No activity recorded yet. Saving changes here starts the history.
          </p>
        )}

        {!activitiesQuery.isLoading && !activitiesQuery.isError && activities.length > 0 && (
          <ul className="space-y-3">
            {activities.map((activity) => (
              <li
                key={activity.id}
                className="border-b border-border pb-3 last:border-0 last:pb-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {formatActivityType(activity.activity_type)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatUiRelativeTime(activity.created_at)}
                  </span>
                </div>
                {activity.description && (
                  <p
                    className="mt-1 whitespace-pre-wrap break-words text-sm"
                    data-content-origin="domain"
                  >
                    {activity.description}
                  </p>
                )}
                {/*
                  A recorded reply keeps its full text in `metadata.body`. The
                  description is only a one-line summary, and a conversation you
                  cannot read back is the thing this timeline exists to fix.
                */}
                {activityBody(activity) && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                      Read the full message
                    </summary>
                    <p
                      className="mt-2 whitespace-pre-wrap break-words rounded-md border border-border bg-surface p-3 text-sm"
                      data-content-origin="user"
                    >
                      {activityBody(activity)}
                    </p>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
